<template>
  <v-alert
    v-if="showAlert"
    :type="alertType"
    :class="['mx-auto','text-center','bg-' + (alertType === 'success' ? 'green-lighten-4' : 'red-lighten-4')]"
    style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
    elevation="2"
    prominent
  >
    {{ alertMessage }}
  </v-alert>

  <v-container fluid>
    <v-card class="pa-4" elevation="2">
      <v-row class="align-center">
        <v-col class="headline mb-4">
          <h2>Carga de material para los equipos</h2>
        </v-col>

        <v-col cols="12" sm="6" class="text-right">
          <v-btn
            color="primary"
            class="mr-2"
            :loading="isExportingPDF"
            :disabled="isLoading || isExportingPDF || warehouses.length === 0"
            @click="exportToPDF"
          >
            Exportar PDF
          </v-btn>

          <v-btn
            color="secondary"
            class="mr-2"
            :loading="isExportingDeliveryPDF"
            :disabled="isLoading || isExportingDeliveryPDF || warehouses.length === 0"
            @click="exportDeliveryNotePDF"
          >
            Albarán de entrega
          </v-btn>
          
          <v-btn @click="$router.back()">Volver</v-btn>
        </v-col>
      </v-row>

      <v-overlay :model-value="isLoading" class="align-center justify-center" persistent>
        <v-progress-circular indeterminate size="48" />
      </v-overlay>

      <!-- Filtres: Data i Almacén -->
      <v-row class="mb-3" dense>
        <v-col cols="12" sm="6" md="4">
          <v-menu
            v-model="menuDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ props }">
              <v-text-field
                v-bind="props"
                :model-value="formatHuman(selectedDate)"
                label="Fecha"
                readonly
                density="comfortable"
                append-inner-icon="mdi-calendar"
              />
            </template>

            <v-date-picker
              v-model="selectedDate"
              show-adjacent-months
              @update:modelValue="menuDate = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="selectedWarehouseId"
            :items="warehouseOptions"
            item-title="title"
            item-value="value"
            label="Almacén"
            density="comfortable"
            hint="Selecciona un almacén (opcional)"
          />
        </v-col>
      </v-row>

      <v-data-table
        v-model:expanded="expanded"
        :items="warehouses"
        :headers="headers"
        :loading="isLoading"
        loading-text="Cargando datos..."
        show-expand
        item-value="name"
        class="elevation-1"
        no-data-text="No hay datos disponibles"
      >
        <template #item.unitatsTotals="{ item }">
          {{ formatNumber(item.unitatsTotals) }}
        </template>

        <!-- Fila expandida: campanyes i fullets -->
        <template #expanded-row="{ columns, item }">
          <tr class="bg-grey-lighten-4">
            <td :colspan="columns.length">
              <v-card flat class="pa-3">
                <h3 class="text-subtitle-1 mb-2">Campañas de {{ item.name }}</h3>

                <v-expansion-panels multiple>
                  <v-expansion-panel
                    v-for="(camp, idx) in item.detail"
                    :key="item.name + '-' + idx"
                  >
                    <v-expansion-panel-title>
                      <div class="d-flex w-100 justify-space-between align-start flex-wrap gap-2">
                        <div>
                          <div>
                            <strong>{{ camp.Campanya }}</strong>
                            <span v-if="getCampaignClient(camp)" class="text-medium-emphasis">
                              — {{ getCampaignClient(camp) }}
                            </span>
                          </div>
                          <div v-if="camp.Descripcio" class="text-body-2 text-medium-emphasis mt-1">
                            {{ camp.Descripcio }} - {{ camp.Servei }}
                          </div>
                        </div>
                        <div class="text-caption">
                          Zonas: {{ camp.Fullets?.length || 0 }} ·
                          Folletos: {{ formatNumber(sumUnits(camp.Fullets)) }}
                        </div>
                      </div>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text>
                      <v-data-table
                        :items="camp.Fullets || []"
                        :headers="leafletHeaders"
                        density="compact"
                        class="elevation-0"
                        hide-default-footer
                      >
                        <template #item.Unitats="{ item }">
                          {{ formatNumber(item.Unitats) }}
                        </template>
                      </v-data-table>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useAlert } from '@/composables/useAlert'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const router = useRouter()
const { alertMessage, alertType, showAlert, showAlertMessage } = useAlert()

const isLoading = ref(false)
const isExportingPDF = ref(false)
const isExportingDeliveryPDF = ref(false)
const warehouses = ref([])
const expanded = ref([])

// 🔹 Calendari
const menuDate = ref(false)
const getTomorrow = () => {
  const d = new Date()
  // Evita problemes d’horari d’estiu: normalitza a mitjanit local
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 1)
  return d
}
const selectedDate = ref(getTomorrow())


// 🔹 Selector d'almacén
const selectedWarehouseId = ref(null) // null = sense filtre
const warehouseOptions = [
  { title: 'Barcelona', value: 453 },
  { title: 'Madrid', value: 502 }
]

// Utils de data i format
const pad = (n) => String(n).padStart(2, '0')
const toISO = (d) => {
  if (!d) return null
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`
}
const formatHuman = (d) => {
  if (!d) return ''
  return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

// Capçaleres
const headers = [
  { title: 'Almacén', value: 'name', sortable: true },
  { title: 'Unidades totales', value: 'unitatsTotals', sortable: true }
]

const leafletHeaders = [
  { title: 'Ref. folleto', value: 'Ref_fullet' },
  { title: 'Unidades', value: 'Unitats' },
  { title: 'Notas', value: 'Notes' }
]

// Helpers
const formatNumber = (n) => new Intl.NumberFormat('es-ES').format(Number(n || 0))
// Suma unitats d'un array de fullets: [{ Unitats: number | string }, ...]
function sumUnits(fullets) {
  if (!Array.isArray(fullets)) return 0
  return fullets.reduce((acc, f) => {
    // Accepta number o string; null/undefined/NaN -> 0
    const raw = f?.Unitats
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10)
    return acc + (Number.isFinite(n) ? n : 0)
  }, 0)
}

const transformData = (dataArr) => {
  const rows = []

  const listEquips = Array.isArray(dataArr) ? dataArr : []
  for (const equip of listEquips) {
    const name = String(equip?.equipNom ?? '—')
    const items = Array.isArray(equip?.items) ? equip.items : []

    const unitatsTotals = items.reduce(
      (acc, camp) => acc + sumUnits(Array.isArray(camp?.Fullets) ? camp.Fullets : []),
      0
    )

    rows.push({
      name,
      equipId: equip?.equipId ?? null,
      equipEmail: equip?.equipEmail ?? null,
      numCampanyes: items.length,
      numFullets: items.reduce((a, c) => a + (Array.isArray(c.Fullets) ? c.Fullets.length : 0), 0),
      unitatsTotals,
      detail: items
    })
  }

  // 🔴 Filtrar proveïdors sense càrrega
  const filtered = rows.filter(r => r.unitatsTotals > 0)

  // Ordenar després del filtre
  filtered.sort((a, b) => a.name.localeCompare(b.name, 'es'))

  return filtered
}


const getCampaignClient = (camp) => {
  const list = Array.isArray(camp?.Fullets) ? camp.Fullets : []
  const unique = [...new Set(list.map(f => f?.Client).filter(Boolean))]
  if (unique.length === 0) return null
  if (unique.length === 1) return unique[0]
  return 'Varios clientes'
}

const ENDPOINT = '/api/admin/planimetria/magatzem'  
const DATE_PARAM_NAME = 'data'

const fetchLiveWarehouses = async () => {
  isLoading.value = true
  try {
    const params = { [DATE_PARAM_NAME]: toISO(selectedDate.value) }
    if (selectedWarehouseId.value != null) params.delegacio_id = selectedWarehouseId.value

    const { data } = await axios.get(ENDPOINT, {
      headers: { 'Accept': 'application/json' },
      timeout: 20000,
      params
    })

    warehouses.value = transformData(data)

  } catch (e) {
    console.error('Error carregant dades en viu:', e)
    showAlertMessage('Error carregant dades de magatzem (Laravel/Odoo).', 'error')
  } finally {
    isLoading.value = false
  }
}


// Auto-refresh en canviar la data o l’almacén
watch(selectedDate, () => {
  if (!selectedDate.value) return
  fetchLiveWarehouses()
})
watch(selectedWarehouseId, () => {
  fetchLiveWarehouses()
})

onMounted(fetchLiveWarehouses)

// =====================
// Exportació a PDF
// =====================

// Agrupa fullets per Ref_fullet i suma Unitats
const buildAggregatedLeaflets = (w) => {
  const buckets = new Map()

  for (const camp of (w.detail || [])) {
    const clientFromCamp = getCampaignClient(camp)
    const servei = (camp.Servei || '').trim() 

    for (const f of (camp.Fullets || [])) {
      const ref = (f.Ref_fullet || '').trim()   // p.ex. "MARTORELL - BLACK FRIDAY MINI"
      if (!ref) continue

      const client = (f.Client || clientFromCamp || '').trim()

      // Unitats pot venir com a string "21.280" → netegem punts
      const rawUnits = f.Unitats
      let units = 0
      if (typeof rawUnits === 'number') {
        units = rawUnits
      } else if (rawUnits != null) {
        units = parseInt(String(rawUnits).replace(/\./g, ''), 10)
      }
      if (!Number.isFinite(units)) units = 0

      // Clau = Ref_fullet
      if (!buckets.has(ref)) {
        buckets.set(ref, {
          ref,
          clients: new Set(),
          serveis: new Set(),
          totalUnits: 0,
        })
      }

      const bucket = buckets.get(ref)
      bucket.totalUnits += units
      if (client) bucket.clients.add(client)
      if (servei) bucket.serveis.add(servei)
    }
  }

  // Normalitzem client i servei finals
  const rows = Array.from(buckets.values()).map(b => {
    let clientName = ''
    if (b.clients.size === 1) {
      clientName = Array.from(b.clients)[0]
    } else if (b.clients.size > 1) {
      clientName = 'Varios clientes'
    }

    let serveiName = ''
    if (b.serveis.size === 1) {
      serveiName = Array.from(b.serveis)[0]
    } else if (b.serveis.size > 1) {
      //serveiName = 'Varios servicios'
      // 🔹 concatenem tots els serveis, ordenats
      const serveisList = Array.from(b.serveis).sort((a, b) =>
        a.localeCompare(b, 'es')
      )
      serveiName = serveisList.join(', ')
    }

    return {
      ref: b.ref,
      client: clientName,
      servei: serveiName,
      totalUnits: b.totalUnits
    }
  })

  // Ordenem per Ref_fullet
  rows.sort((a, b) => (a.ref || '').localeCompare(b.ref || '', 'es'))

  return rows
}


const exportToPDF = async () => {
  try {
    isExportingPDF.value = true

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()
    let cursorY = 40

    // Capçalera global
    doc.setFontSize(14)
    doc.text('Carga de material para los equipos', pageWidth / 2, cursorY, { align: 'center' })
    cursorY += 18
    doc.setFontSize(10)
    doc.text(`Fecha: ${formatHuman(selectedDate.value)}`, pageWidth / 2, cursorY, { align: 'center' })
    cursorY += 14
    const warehouseName = warehouseOptions.find(o => o.value === selectedWarehouseId.value)?.title || 'Todos'
    doc.text(`Almacén: ${warehouseName}`, pageWidth / 2, cursorY, { align: 'center' })
    cursorY += 14

    // Per cada magatzem, una secció
    warehouses.value.forEach((w, idx) => {
      if (idx > 0) {
        doc.addPage()
        cursorY = 40
      }

      // Títol magatzem + totals
      doc.setFontSize(12)
      doc.text(`Almacén: ${w.name}`, 40, cursorY)
      cursorY += 16
      doc.setFontSize(10)
      doc.text(`Unidades totales: ${formatNumber(w.unitatsTotals)}`, 40, cursorY)
      cursorY += 10
     
      // ---------- AGRUPACIÓ PER TIPUS DE FULLET ----------
            // ---------- AGRUPACIÓ PER REF_FULLET ----------
      const aggregated = buildAggregatedLeaflets(w)

      const bodyRows = aggregated.map(row => [
        row.ref || '',                 // Ref_fullet (JUEVES LOCOS, MARTORELL - BLACK FRIDAY MINI, ...)
        row.client || '',              // Client ((Caprabo) CAPRABO, S.A., Carrefour Central, ...)
        row.servei || '',              // Servei d'Odoo (Entrega en tienda (ET), ...)
        formatNumber(row.totalUnits),  // Total unitats sumades
      ])

      autoTable(doc, {
        startY: cursorY + 6,
        head: [['Folleto', 'Cliente', 'Servicio', 'Unidades']],
        body: bodyRows,
        styles: { fontSize: 9 },
        headStyles: { fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 150 },              // Folleto (Ref_fullet)
          1: { cellWidth: 150 },              // Cliente
          2: { cellWidth: 150 },              // Servei
          3: { cellWidth: 60, halign: 'right' } // Total unidades
        },
        didDrawPage: () => {
          const str = `Página ${doc.internal.getNumberOfPages()}`
          doc.setFontSize(9)
          doc.text(str, pageWidth - 40, doc.internal.pageSize.getHeight() - 20, { align: 'right' })
        }
      })



    })

    const stamp = new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)
    doc.save(`carga_material_${stamp}.pdf`)
  } catch (e) {
    console.error('Error exportando PDF', e)
    showAlertMessage('No se ha podido exportar el PDF.', 'error')
  } finally {
    isExportingPDF.value = false
  }
}

const formatDateDmyDashed = (d) => {
  if (!d) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}` // 19-11-2025
}

const LOGO_BASE64 = "data:image/png;base64,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"

const exportDeliveryNotePDF = async () => {
  try {
    isExportingDeliveryPDF.value = true

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    const dateStr = formatDateDmyDashed(selectedDate.value || new Date())

    warehouses.value.forEach((w, idx) => {
      if (idx > 0) {
        doc.addPage()
      }

      let cursorY = 40

      // ---------- LOGO + TÍTOL ----------
      const logoX = 40
      const logoY = 40
      const logoW = 80
      const logoH = 120

      if (LOGO_BASE64) {
        try {
          doc.addImage(LOGO_BASE64, 'PNG', logoX, logoY, logoW, logoH)
        } catch (e) {
          // si el logo falla, simplement el saltem
          console.warn('No s’ha pogut dibuixar el logo a l’albarà', e)
        }
      }

      // Títol a la dreta del logo
      doc.setFontSize(16)
      doc.text('Albarán de entrega', logoX + logoW + 30, logoY + 20)

      doc.setFontSize(11)
      const providerName = w.name || 'Proveedor'
      doc.text(`${providerName}`, logoX + logoW + 30, logoY + 40)

      cursorY = logoY + logoH + 30 // baixem sota el logo

      // ---------- TAULA (mateixa que al resum) ----------
      const aggregated = buildAggregatedLeaflets(w)

      const bodyRows = aggregated.map(row => [
        row.ref || '',                // Folleto
        row.client || '',             // Cliente
        formatNumber(row.totalUnits), // Total unidades
      ])

      autoTable(doc, {
        startY: cursorY,
        head: [['Folleto', 'Cliente', 'Total unidades']],
        body: bodyRows,
        styles: { fontSize: 9 },
        headStyles: { fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 220 },              // Folleto
          1: { cellWidth: 220 },              // Cliente
          2: { cellWidth: 70, halign: 'right' } // Total unidades
        },
        margin: { left: 40, right: 40 },
        didDrawPage: () => {
          const str = `Página ${doc.internal.getNumberOfPages()}`
          doc.setFontSize(9)
          doc.text(str, pageWidth - 40, pageHeight - 20, { align: 'right' })
        }
      })

      const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || (cursorY + 120)
      const blockY = Math.min(finalY + 40, pageHeight - 140) // una mica per sobre peu pàgina

      // ---------- BLOC DADES ENTREGA / RECOGE ----------
      doc.setFontSize(11)

      const leftX = 40
      const rightX = pageWidth / 2 + 20

      // Columna 1: Entrega
      doc.setFont(undefined, 'bold')
      doc.text('Entrega:', leftX, blockY)
      doc.setFont(undefined, 'normal')
      doc.text('TRAYCCO PUBLICITAT', leftX, blockY + 14)
      doc.text('Teléfono: 902 40 40 32', leftX, blockY + 28)
      doc.text(`Fecha: ${dateStr}`, leftX, blockY + 42)
      doc.text('Firma:', leftX, blockY + 66)

      // Columna 2: Recoge
      doc.setFont(undefined, 'bold')
      doc.text('Recoge:', rightX, blockY)
      doc.setFont(undefined, 'normal')
      doc.text(providerName, rightX, blockY + 14)
      doc.text(`Fecha: ${dateStr}`, rightX, blockY + 28)
      doc.text('Firma:', rightX, blockY + 66)
    })

    const stamp = new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)
    doc.save(`albaran_entrega_material_${stamp}.pdf`)
  } catch (e) {
    console.error('Error exportando albarán PDF', e)
    showAlertMessage('No se ha podido exportar el albarán.', 'error')
  } finally {
    isExportingDeliveryPDF.value = false
  }
}


</script>
